import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/lab';
import {
  Alert,
  Autocomplete,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  SxProps,
  TextFieldProps,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import moment, { Moment } from 'moment';
import AdapterMoment from '@mui/lab/AdapterMoment';

import {
  classDurationOptions,
  TZoomMeetingSectionPaymentTypeOption,
  zoomMeetingSectionPaymentTypeOptions,
} from '../../../../../../constants';
import {
  ISection,
  LESSON_TYPES,
  TCreateCourseZoomSectionAsAdmin,
  createNewCourseZoomSectionAsAdmin,
} from '../../../../../../store/V2/SectionSlice';
import { getCoursesState } from '../../../../../../store/V2/CoursesSlice';
import { useAppSelector } from '../../../../../../hooks';
import {
  defaultTextFieldProps,
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import { TSubsectionCreateNewItemRequestData } from '../../components/SectionItem';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import {
  capitalizeFirstLetter,
  getFormatedDurationByMinutes,
  getShortName,
  isCourseTutorEligibleForClass,
} from '../../../../../../helper';
import {
  COURSE_TUTOR_TYPES,
  getCourseTutorsState,
  TCourseTutorType,
} from '../../../../../../store/V2/CourseTutorsSlice';
import { getCourseStudentsState } from '../../../../../../store/V2/CourseStudentsSlice';
import {
  getUserByIdAsAdmin,
  getUsersState,
  resetUser,
} from '../../../../../../store/UserSlice';

interface AddZoomSectionItemModalProps {
  open: boolean;
  onClose: () => void;
  parentSectionItemData?: TSubsectionCreateNewItemRequestData['parentSection'];
  mode?: 'edit';
  section?: ISection;
}

type TSelectOption = {
  label: string;
  value: string;
  type?: TCourseTutorType;
};

type TState = {
  title: string;
  dateTime: Moment | null;
  classDuration: string;
  selectedTutorOption: TSelectOption | null;
  selectedPaymentType: TZoomMeetingSectionPaymentTypeOption;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const oneToOneStudentDetailsViewerStyles: SxProps = {
  backgroundColor: '#F8F9FD',
  borderRadius: '7px',
};

const initialState: TState = {
  title: '',
  dateTime: null,
  classDuration: '60',
  selectedTutorOption: null,
  selectedPaymentType: zoomMeetingSectionPaymentTypeOptions[0],
};

const AddZoomSectionItemModal: React.FC<AddZoomSectionItemModalProps> = ({
  open,
  onClose,
  parentSectionItemData,
  mode,
  section,
}) => {
  const dispatch = useDispatch();

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const courseStudentsState = useAppSelector(getCourseStudentsState);
  const courseTutorsState = useAppSelector(getCourseTutorsState);
  const usersState = useAppSelector(getUsersState);

  // State
  const [state, setState] = useState(initialState);
  const [tutorOptions, setTutorOptions] = useState<TSelectOption[]>([]);
  const [isLoading, setLoading] = useState(false);

  const { user: oneToOneStudentData } = usersState;

  const alerts: string[] = [];
  const courseData = coursesState.adminCourse;
  const courseTutors =
    courseTutorsState.adminCourseTutorsData &&
    courseTutorsState.adminCourseTutorsData.list &&
    courseTutorsState.adminCourseTutorsData.list.length
      ? courseTutorsState.adminCourseTutorsData.list
      : [];

  const courseStudents =
    courseStudentsState.adminCourseStudentsData &&
    courseStudentsState.adminCourseStudentsData.list &&
    courseStudentsState.adminCourseStudentsData.list.length
      ? courseStudentsState.adminCourseStudentsData.list.filter(
          (item) => item.status === 'ENABLE'
        )
      : [];

  let oneToOneStudentName = '';
  let oneToOneStudentId = '';
  let oneToOneStudentRemainingTime = '';
  let isSubmitDisabled =
    !state.title.trim().length ||
    !state.dateTime ||
    !(Number(state.classDuration) > 0) ||
    !state.selectedTutorOption;

  if (courseData && courseData.isOneToOne && !courseStudents.length) {
    alerts.push('Please add students.');
    isSubmitDisabled = true;
  }

  if (courseStudents.length === 1) {
    const _student = courseStudents[0].student;

    if (_student) {
      oneToOneStudentId = _student._id;
      oneToOneStudentName = getShortName(
        _student.firstname,
        _student.lastname,
        true
      );
    }
  }

  if (!courseTutors.length) {
    alerts.push('Please add tutors.');
    isSubmitDisabled = true;
  }

  if (oneToOneStudentData) {
    oneToOneStudentRemainingTime = getFormatedDurationByMinutes({
      minutes: oneToOneStudentData.remainingTime || 0,
    });
  }

  useEffect(() => {
    if (oneToOneStudentId) {
      dispatch(getUserByIdAsAdmin(oneToOneStudentId));
    }
    // NOT UTILIZING
    // if (mode === 'edit' && section) {
    //   setState((prevState) => {
    //     const newState: TState = {
    //       ...prevState,
    //       title: section.title,
    //     };
    //     if (section.dateTime) {
    //       newState.dateTime = moment(section.dateTime);
    //     }
    //     if (section.duration) {
    //       newState.classDuration = section.duration.toString();
    //     }
    //     return newState;
    //   });
    // }

    return () => {
      dispatch(resetUser());
    };
  }, []);

  useEffect(() => {
    updateTutorOptions();
  }, [state.dateTime, state.classDuration]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const updateTutorOptions = () => {
    if (state.classDuration && state.dateTime) {
      const newTutorOptions: TSelectOption[] = [];

      courseTutors.forEach((courseTutor) => {
        let proceed = false;

        if (state.dateTime && state.classDuration) {
          proceed = isCourseTutorEligibleForClass(
            courseTutor,
            state.dateTime.toISOString(),
            parseFloat(state.classDuration)
          );
        }

        if (proceed) {
          const _tutor = courseTutor.tutor;

          let _label = getShortName(_tutor.firstname, _tutor.lastname, true);

          if (courseTutor.type) {
            _label += ` (${capitalizeFirstLetter(
              courseTutor.type.toLowerCase()
            )})`;
          }

          newTutorOptions.push({
            value: _tutor._id,
            label: _label,
            type: courseTutor.type,
          });
        }
      });

      setTutorOptions(newTutorOptions);

      let _selectedTutorOption: TSelectOption | null = null;

      if (newTutorOptions.length) {
        // eslint-disable-next-line prefer-destructuring
        _selectedTutorOption = newTutorOptions[0];

        const _matchingPrimaryOption = newTutorOptions.find(
          (item) => item.type === COURSE_TUTOR_TYPES.PRIMARY
        );

        if (_matchingPrimaryOption) {
          _selectedTutorOption = { ..._matchingPrimaryOption };
        }
      }

      updateStateAttributes({
        selectedTutorOption: _selectedTutorOption,
      });
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (courseData && state.dateTime && state.classDuration) {
      if (mode === 'edit' && section) {
        // TODO
      } else if (
        parentSectionItemData &&
        state.selectedTutorOption &&
        state.selectedPaymentType
      ) {
        setLoading(true);

        const apiData: TCreateCourseZoomSectionAsAdmin = {
          parent: parentSectionItemData._id,
          sortingKey: parentSectionItemData.nextSortingKey,
          course: courseData._id,
          title: state.title.trim(),
          dateTime: state.dateTime.clone().toISOString(),
          duration: Number(state.classDuration),
          tutor: state.selectedTutorOption.value,
          paymentType: state.selectedPaymentType.value,
          lessonType: LESSON_TYPES.LESSON,
        };

        await dispatch(createNewCourseZoomSectionAsAdmin(apiData));

        setLoading(false);
      }
    }

    onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: '575px',
          borderRadius: '20px',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {mode === 'edit' ? 'Edit' : 'Zoom'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: alerts.length ? 0 : '6px' }}>
          <Grid container spacing={2}>
            {alerts.length ? (
              <Grid item xs={12}>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    backgroundColor: 'warning.light',
                    color: 'warning.main',
                    mb: '15px',
                  }}
                >
                  <ul style={{ margin: 0 }}>
                    {alerts.map((item, index) => {
                      return (
                        <Typography
                          key={`alert-li-${index}`}
                          component="li"
                          variant="subtitle2"
                        >
                          {item}
                        </Typography>
                      );
                    })}
                  </ul>
                </Alert>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <MobileDateTimePicker
                    value={state.dateTime}
                    minDateTime={moment()}
                    inputFormat="YYYY-MM-DD hh:mm A"
                    label="Date Time *"
                    InputProps={{ readOnly: true }}
                    onChange={(value) => {
                      if (value) {
                        updateStateAttributes({
                          dateTime: value.startOf('minute'),
                        });
                      }
                    }}
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        placeholder="Select"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              <DateRangeIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  select
                  {...textFieldProps}
                  variant="outlined"
                  value={state.classDuration}
                  label="Class Duration *"
                  onChange={(event) =>
                    updateStateAttributes({ classDuration: event.target.value })
                  }
                >
                  {classDurationOptions.map((durationOption, index) => {
                    return (
                      <MenuItem
                        key={`opt-${index}`}
                        value={durationOption.value}
                      >
                        {durationOption.label}
                      </MenuItem>
                    );
                  })}
                </TPTextField>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  // disableClearable
                  size="small"
                  value={state.selectedTutorOption}
                  options={tutorOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  disabled={!state.dateTime || !state.classDuration}
                  onChange={(e, value) =>
                    updateStateAttributes({ selectedTutorOption: value })
                  }
                  sx={{
                    '& .MuiAutocomplete-clearIndicator': {
                      display: 'none',
                    },
                  }}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...defaultTextFieldProps}
                      size="small"
                      label="Tutor *"
                      placeholder="Select"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            {oneToOneStudentName ? (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TPTextField
                    {...textFieldProps}
                    name="title"
                    size="small"
                    label="Student"
                    defaultValue={oneToOneStudentName}
                    InputProps={{ readOnly: true }}
                  />
                </FormControl>
              </Grid>
            ) : null}
            {oneToOneStudentName && oneToOneStudentRemainingTime ? (
              <Grid item xs={12}>
                <Card elevation={0} sx={oneToOneStudentDetailsViewerStyles}>
                  <CardContent style={{ padding: '8px 16px' }}>
                    <Typography fontSize="14px" fontWeight={600}>
                      Credits: {oneToOneStudentRemainingTime}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  name="title"
                  size="small"
                  label="Title *"
                  placeholder="Title"
                  value={state.title}
                  onChange={handleInputChange}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  disableClearable
                  size="small"
                  value={state.selectedPaymentType}
                  options={zoomMeetingSectionPaymentTypeOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  onChange={(e, value) =>
                    updateStateAttributes({ selectedPaymentType: value })
                  }
                  getOptionLabel={(option) =>
                    capitalizeFirstLetter(
                      option.label.toLowerCase().replace('_', ' ')
                    )
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...defaultTextFieldProps}
                      size="small"
                      label="Payment Type *"
                      placeholder="Select"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
        <DarkLoadingButton
          disableElevation
          variant="contained"
          color="inherit"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          loading={isLoading}
          sx={{ ...defaultButtonStyles }}
        >
          {mode === 'edit' ? 'Save' : 'Add'}
        </DarkLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddZoomSectionItemModal;
